<template>
  <div class="animated fadeIn">

    <!-- Header -->
    <b-row class="mb-3 align-items-center">
      <b-col>
        <h4 class="mb-0"><i class="fa fa-users mr-2 text-info"></i>Tech Team Members</h4>
        <small class="text-muted">Active staff available for issue assignment · sourced live from HRM</small>
      </b-col>
      <b-col class="text-right">
        <b-button variant="outline-secondary" size="sm" @click="loadMembers" :disabled="loading">
          <i class="icon-reload mr-1"></i>Refresh
        </b-button>
      </b-col>
    </b-row>

    <!-- Stat strip -->
    <b-row class="mb-3">
      <b-col sm="4" class="mb-2">
        <div class="tm-stat tm-stat-primary">
          <div class="tm-stat-num">{{ members.length }}</div>
          <div class="tm-stat-label"><i class="fa fa-users mr-1"></i>Total Members</div>
        </div>
      </b-col>
      <b-col sm="4" class="mb-2">
        <div class="tm-stat tm-stat-success">
          <div class="tm-stat-num">{{ withPhotoCount }}<span class="tm-stat-of">/{{ members.length }}</span></div>
          <div class="tm-stat-label"><i class="fa fa-camera mr-1"></i>With Photo</div>
        </div>
      </b-col>
      <b-col sm="4" class="mb-2">
        <div class="tm-stat tm-stat-info">
          <div class="tm-stat-num">{{ deptCount }}</div>
          <div class="tm-stat-label"><i class="fa fa-sitemap mr-1"></i>Departments</div>
        </div>
      </b-col>
    </b-row>

    <!-- Toolbar -->
    <b-card no-body class="shadow-sm mb-3">
      <div class="p-3">
        <b-row class="align-items-center">
          <b-col md="7" class="mb-2 mb-md-0">
            <b-input-group size="sm">
              <b-input-group-prepend is-text><i class="fa fa-search"></i></b-input-group-prepend>
              <b-form-input v-model="filter" type="search"
                            placeholder="Search name, ID, email, department, designation…" />
              <b-input-group-append>
                <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col md="3" sm="7" class="mb-2 mb-md-0">
            <b-input-group size="sm" prepend="Per page">
              <b-form-select v-model="perPage" :options="perPageOptions" />
            </b-input-group>
          </b-col>
          <b-col md="2" sm="5" class="text-md-right text-muted small">
            <span v-if="totalRows">{{ rangeStart }}–{{ rangeEnd }} of {{ totalRows }}</span>
            <span v-else>0 results</span>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5 text-muted">
      <b-spinner variant="info" /> Loading team members…
    </div>

    <!-- Empty -->
    <div v-else-if="totalRows === 0" class="text-center py-5 text-muted">
      <i class="fa fa-user-times fa-2x mb-2 d-block"></i>
      <span v-if="filter">No members match “{{ filter }}”.</span>
      <span v-else>No active team members found for the tech office.</span>
    </div>

    <!-- Member card grid -->
    <b-row v-else>
      <b-col sm="6" lg="4" xl="3" v-for="m in paged" :key="m.id" class="mb-3">
        <div class="member-card">
          <div class="mc-top">
            <div class="mc-avatar">
              <img v-if="hasPhoto(m)" :src="m.picture_url" class="mc-photo" alt="" @error="onImgError(m.id)" />
              <span v-else class="mc-letter" :style="{ background: avatarColor(m) }">{{ initials(m) }}</span>
              <span class="mc-dot" title="Active"></span>
            </div>
            <div class="mc-id">
              <div class="mc-name" :title="m.full_name">{{ m.full_name }}</div>
              <div v-if="m.designation" class="mc-desig" :title="m.designation">{{ m.designation }}</div>
              <div class="mc-user">{{ m.username }}</div>
            </div>
          </div>

          <div class="mc-meta" v-if="m.department || m.email">
            <div v-if="m.department" class="mc-line">
              <i class="fa fa-sitemap"></i>
              <span class="mc-clip">{{ m.department }}<span v-if="m.sub_department" class="text-muted"> · {{ m.sub_department }}</span></span>
            </div>
            <div v-if="m.email" class="mc-line">
              <i class="fa fa-envelope-o"></i>
              <a :href="'mailto:' + m.email" class="mc-email mc-clip" :title="m.email">{{ m.email }}</a>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Pagination -->
    <div v-if="!loading && totalRows > perPage" class="d-flex justify-content-center mt-2">
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        align="center"
        pills
        class="mb-0"
      />
    </div>

  </div>
</template>

<script>
import teamMemberService from '@/services/teamMemberService'

// White-legible mid-tone palette for letter avatars (deterministic per person).
var AVATAR_COLORS = ['#20a8d8', '#4dbd74', '#6f42c1', '#f86c6b', '#20c997', '#e0567f']
var SEARCH_KEYS = ['full_name', 'username', 'email', 'department', 'sub_department', 'designation']

export default {
  name: 'TeamMembers',
  data: function() {
    return {
      loading: false,
      members: [],
      brokenImages: {},
      filter: '',
      perPage: 24,
      perPageOptions: [12, 24, 48, 96],
      currentPage: 1
    }
  },
  computed: {
    withPhotoCount: function() {
      return this.members.filter(function(m) { return !!m.picture_url }).length
    },
    deptCount: function() {
      var seen = {}
      this.members.forEach(function(m) { if (m.department) { seen[m.department] = true } })
      return Object.keys(seen).length
    },
    filteredMembers: function() {
      var q = (this.filter || '').trim().toLowerCase()
      if (!q) { return this.members }
      return this.members.filter(function(m) {
        return SEARCH_KEYS.some(function(k) {
          return m[k] && String(m[k]).toLowerCase().indexOf(q) !== -1
        })
      })
    },
    totalRows: function() { return this.filteredMembers.length },
    pageCount: function() { return Math.ceil(this.totalRows / this.perPage) || 1 },
    paged: function() {
      var start = (this.currentPage - 1) * this.perPage
      return this.filteredMembers.slice(start, start + this.perPage)
    },
    rangeStart: function() { return this.totalRows ? (this.currentPage - 1) * this.perPage + 1 : 0 },
    rangeEnd: function() { return Math.min(this.currentPage * this.perPage, this.totalRows) }
  },
  watch: {
    filter: function() { this.currentPage = 1 },
    perPage: function() { this.currentPage = 1 }
  },
  created: function() { this.loadMembers() },
  methods: {
    loadMembers: function() {
      var self = this
      self.loading = true
      teamMemberService.listAll(
        function(data) {
          self.members = data || []
          self.brokenImages = {}
          self.currentPage = 1
          self.loading = false
        },
        function(err) {
          self.loading = false
          self.$bvToast.toast(err, { title: 'Error', variant: 'danger', solid: true })
        }
      )
    },
    hasPhoto: function(m) {
      return m.picture_url && !this.brokenImages[m.id]
    },
    onImgError: function(id) {
      // Photo URL missing/broken → fall back to the initial-letter avatar.
      this.$set(this.brokenImages, id, true)
    },
    initials: function(m) {
      var n = (m.full_name || '').trim()
      if (!n) { return '?' }
      var parts = n.split(/\s+/)
      if (parts.length === 1) { return parts[0].charAt(0).toUpperCase() }
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
    },
    avatarColor: function(m) {
      var key = m.full_name || m.username || ''
      var sum = 0
      for (var i = 0; i < key.length; i++) { sum += key.charCodeAt(i) }
      return AVATAR_COLORS[sum % AVATAR_COLORS.length]
    }
  }
}
</script>

<style scoped>
/* Stat tiles */
.tm-stat { background:#fff; border:1px solid #e4e7ea; border-radius:8px; padding:14px 16px;
           box-shadow:0 .125rem .25rem rgba(0,0,0,.075); }
.tm-stat-num { font-size:1.7rem; font-weight:700; line-height:1; color:#23282c; }
.tm-stat-of { font-size:1rem; color:#a4b7c1; font-weight:400; }
.tm-stat-label { font-size:.72rem; text-transform:uppercase; letter-spacing:.04em; color:#8a93a2; margin-top:6px; }
.tm-stat-primary { border-left:4px solid #20a8d8; }
.tm-stat-success { border-left:4px solid #4dbd74; }
.tm-stat-info    { border-left:4px solid #63c2de; }

/* Member card */
.member-card { background:#fff; border:1px solid #e4e7ea; border-radius:10px; padding:14px;
               height:100%; box-shadow:0 .125rem .25rem rgba(0,0,0,.075);
               transition:box-shadow .15s ease, transform .15s ease; }
.member-card:hover { box-shadow:0 .5rem 1rem rgba(0,0,0,.12); transform:translateY(-2px); }

.mc-top { display:flex; align-items:center; }
.mc-avatar { position:relative; flex-shrink:0; margin-right:12px; }
.mc-photo, .mc-letter { width:52px; height:52px; border-radius:50%; display:block; }
.mc-photo  { object-fit:cover; background:#e4e7ea; border:1px solid #e4e7ea; }
.mc-letter { display:inline-flex; align-items:center; justify-content:center;
             color:#fff; font-weight:700; font-size:1.15rem; }
.mc-dot { position:absolute; right:1px; bottom:1px; width:13px; height:13px; border-radius:50%;
          background:#4dbd74; border:2px solid #fff; }

.mc-id { min-width:0; }   /* allows ellipsis inside flex */
.mc-name  { font-weight:700; color:#23282c; line-height:1.25; }
.mc-desig { font-size:.8rem; color:#20a8d8; font-weight:600; }
.mc-user  { font-size:.72rem; color:#8a93a2; font-family:monospace; margin-top:1px; }

.mc-meta { margin-top:12px; padding-top:10px; border-top:1px solid #f0f3f5; }
.mc-line { display:flex; align-items:center; font-size:.8rem; color:#6a7887; margin-bottom:4px; }
.mc-line:last-child { margin-bottom:0; }
.mc-line i { width:16px; text-align:center; margin-right:8px; color:#a4b7c1; flex-shrink:0; }
.mc-email { color:#6a7887; text-decoration:none; }
.mc-email:hover { color:#20a8d8; text-decoration:underline; }

/* Truncate long text to keep cards tidy */
.mc-name, .mc-desig { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:100%; }
.mc-clip { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; flex:1 1 auto; min-width:0; }
</style>
